'use client';

import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Globe,
  Menu,
  X,
  ChevronRight,
  ChevronDown,
  Linkedin,
  Youtube,
  Twitter,
  ShieldCheck,
  Mail,
  Phone,
  MapPin,
  Eye,
  EyeOff,
  LogIn,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { signIn } from '@/lib/auth/client';
import { ensureSessionReady } from '@/lib/auth/ensure-session-ready';
import { useSession } from '@/hooks/useSession';

const navLinks = [
  {
    label: 'Industries',
    children: [
      { href: '/industries/insurance-ubi', label: 'Insurance & UBI' },
      { href: '/industries/ambulance-emergency', label: 'Ambulance & Emergency' },
      { href: '/industries/livestock-farming', label: 'Livestock & Farming' },
      { href: '/industries/fleet-management', label: 'Fleet Management' },
      { href: '/industries/logistics', label: 'Logistics & Supply Chain' },
    ],
  },
  { href: '/features', label: 'Features' },
  { href: '/gadgets', label: 'Hardware' },
  { href: '/white-label', label: 'White-Label' },
  { href: '/pricing', label: 'Pricing' },
  { href: '/blog', label: 'Blog' },
  { href: '/contact', label: 'Contact' },
];

const footerColumns = [
  {
    title: 'Platform',
    links: [
      { href: '/features/live-tracking', label: 'Live Tracking' },
      { href: '/features/fleet-management', label: 'Fleet Management' },
      { href: '/features/camera-intelligence', label: 'Camera Intelligence' },
      { href: '/features/ai-copilot', label: 'AI Copilot' },
      { href: '/features/geofencing', label: 'Geofencing' },
      { href: '/features/reports-analytics', label: 'Reports & Analytics' },
    ],
  },
  {
    title: 'Industries',
    links: [
      { href: '/industries/insurance-ubi', label: 'Insurance & UBI' },
      { href: '/industries/ambulance-emergency', label: 'Ambulance & Emergency' },
      { href: '/industries/livestock-farming', label: 'Livestock & Farming' },
      { href: '/industries/fleet-management', label: 'Fleet Management' },
      { href: '/industries/logistics', label: 'Logistics & Supply Chain' },
    ],
  },
  {
    title: 'Solutions',
    links: [
      { href: '/white-label', label: 'White-Label Platform' },
      { href: '/gadgets', label: 'Hardware & Devices' },
      { href: '/mobile-app', label: 'Mobile App' },
      { href: '/features/driver-safety', label: 'Driver Safety' },
      { href: '/pricing', label: 'Pricing' },
    ],
  },
  {
    title: 'Company',
    links: [
      { href: '/about', label: 'About Us' },
      { href: '/contact', label: 'Contact' },
      { href: '/blog', label: 'Blog' },
      { href: '/privacy', label: 'Privacy Policy' },
      { href: '/terms', label: 'Terms of Service' },
    ],
  },
];

/* ── Inline Login Dropdown ──────────────────────────────────── */
function LoginDropdown({ open, onClose }: { open: boolean; onClose: () => void }) {
  const router = useRouter();
  const session = useSession();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await signIn({ email, password });
      if (!result.ok) { setError(result.error ?? 'Unable to sign in'); setLoading(false); return; }
      const hydrated = await ensureSessionReady(session);
      if (!hydrated) {
        setError('Sign-in succeeded but session sync is still in progress. Please try again.');
        return;
      }
      onClose();
      router.replace('/app');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to sign in');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          ref={dropdownRef}
          initial={{ opacity: 0, y: -8, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -8, scale: 0.96 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className="absolute right-0 top-full mt-2 w-80 rounded-2xl glass-card-heavy p-6"
        >
          <div className="mb-5">
            <div className="flex items-center gap-2 mb-1">
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-neon-cyan/10 text-neon-cyan">
                <LogIn className="h-3.5 w-3.5" />
              </div>
              <h3 className="text-sm font-semibold text-white">Welcome Back</h3>
            </div>
            <p className="text-xs text-slate-300">Sign in to access your fleet dashboard</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-300">Email</label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" required autoFocus className="h-9 text-sm bg-white/5 border-white/10 text-white placeholder:text-slate-300" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-300">Password</label>
              <div className="relative">
                <Input type={showPw ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter password" required className="h-9 pr-9 text-sm bg-white/5 border-white/10 text-white placeholder:text-slate-300" />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-300 hover:text-white" tabIndex={-1}>
                  {showPw ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                </button>
              </div>
            </div>
            {error && <p className="text-xs text-neon-magenta">{error}</p>}
            <button type="submit" disabled={loading} className="w-full rounded-lg btn-neon px-4 py-2 text-sm">
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <div className="mt-4 flex items-center justify-between border-t border-white/5 pt-3">
            <Link href="/auth/forgot-password" className="text-xs text-slate-300 hover:text-neon-cyan transition" onClick={onClose}>Forgot password?</Link>
            <Link href="/signup" className="text-xs font-semibold text-neon-cyan hover:text-neon-cyan/80 transition" onClick={onClose}>Create account</Link>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* ── Dropdown Menu for nav ─────────────────────────────────── */
function NavDropdown({ item, pathname }: { item: { label: string; children: { href: string; label: string }[] }; pathname: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const isActive = item.children.some(c => pathname.startsWith(c.href));

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
          isActive ? 'text-neon-cyan' : 'text-slate-300 hover:text-white'
        }`}
      >
        {item.label}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.15 }}
            className="absolute left-0 top-full mt-1 w-56 rounded-xl glass-card-heavy py-2 z-50"
          >
            {item.children.map(child => (
              <Link
                key={child.href}
                href={child.href}
                onClick={() => setOpen(false)}
                className={`block px-4 py-2.5 text-sm transition-colors ${
                  pathname === child.href
                    ? 'text-neon-cyan bg-neon-cyan/5'
                    : 'text-slate-300 hover:text-white hover:bg-white/5'
                }`}
              >
                {child.label}
              </Link>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ── Main Shell ─────────────────────────────────────────────── */
export default function MarketingShell({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setLoginOpen(false);
  }, [pathname]);

  return (
    <div className="min-h-screen bg-[var(--background)]">
      {/* ── Navbar ────────────────────────────────────── */}
      <header className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${scrolled ? 'glass-nav glass-nav-scrolled' : 'glass-nav'}`}>
        <nav className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-8">
          <Link href="/" className="flex items-center gap-3 group">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-neon-cyan to-neon-purple text-white shadow-lg transition-transform group-hover:scale-105" style={{ boxShadow: '0 0 20px rgba(0,212,255,0.2)' }}>
              <Globe className="h-5 w-5" />
            </div>
            <div>
              <span className="text-lg font-bold tracking-tight text-white">GlonexTrack</span>
              <p className="text-[10px] font-medium uppercase tracking-[0.2em] text-neon-cyan/60">Telematics Intelligence</p>
            </div>
          </Link>

          <div className="hidden items-center gap-1 lg:flex">
            {navLinks.map((link) =>
              'children' in link && link.children ? (
                <NavDropdown key={link.label} item={link as { label: string; children: { href: string; label: string }[] }} pathname={pathname} />
              ) : (
                <Link
                  key={link.href}
                  href={link.href!}
                  className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                    pathname === link.href ? 'text-neon-cyan' : 'text-slate-300 hover:text-white'
                  }`}
                >
                  {link.label}
                </Link>
              )
            )}
          </div>

          <div className="hidden items-center gap-3 lg:flex">
            <div className="relative">
              <button onClick={() => setLoginOpen(!loginOpen)} className="flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-slate-300 hover:text-white transition-colors">
                <LogIn className="h-4 w-4" />
                Sign In
              </button>
              <LoginDropdown open={loginOpen} onClose={() => setLoginOpen(false)} />
            </div>
            <Link href="/signup">
              <button className="btn-neon rounded-full px-5 py-2.5 text-sm flex items-center gap-2">
                Start Free Trial
                <ChevronRight className="h-4 w-4" />
              </button>
            </Link>
          </div>

          <button onClick={() => setMobileOpen(!mobileOpen)} className="rounded-lg p-2 text-slate-300 hover:text-white lg:hidden" aria-label="Toggle menu">
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </nav>

        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden border-t border-white/5 bg-[var(--background)]/95 backdrop-blur-xl lg:hidden"
            >
              <div className="space-y-1 px-6 py-4">
                {navLinks.map((link) =>
                  'children' in link && link.children ? (
                    <div key={link.label}>
                      <p className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-slate-300">{link.label}</p>
                      {link.children.map(child => (
                        <Link key={child.href} href={child.href} className="block rounded-lg px-6 py-2.5 text-sm text-slate-300 hover:text-white hover:bg-white/5">{child.label}</Link>
                      ))}
                    </div>
                  ) : (
                    <Link key={link.href} href={link.href!} className={`block rounded-lg px-4 py-3 text-sm font-medium ${pathname === link.href ? 'text-neon-cyan bg-neon-cyan/5' : 'text-slate-300 hover:text-white'}`}>{link.label}</Link>
                  )
                )}
                <div className="border-t border-white/5 pt-4 space-y-2">
                  <Link href="/auth/login" className="block">
                    <button className="w-full btn-neon-outline rounded-lg px-4 py-2.5 text-sm">Sign In</button>
                  </Link>
                  <Link href="/signup" className="block">
                    <button className="w-full btn-neon rounded-full px-4 py-2.5 text-sm">Start Free Trial</button>
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      <main className="pt-[72px]">{children}</main>

      {/* ── Footer ───────────────────────────────────── */}
      <footer className="border-t border-white/8 bg-[var(--background)]">
        <div className="mx-auto max-w-7xl px-6 py-16 lg:px-8">
          <div className="grid grid-cols-2 gap-8 md:grid-cols-4 lg:grid-cols-5">
            <div className="col-span-2 md:col-span-4 lg:col-span-1 lg:pr-8">
              <Link href="/" className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-neon-cyan to-neon-purple text-white">
                  <Globe className="h-4 w-4" />
                </div>
                <span className="text-base font-bold text-white">GlonexTrack</span>
              </Link>
              <p className="mt-4 text-sm leading-relaxed text-slate-300">
                Edge-to-cloud telematics intelligence. Born in South Africa, deployed globally.
              </p>
              <div className="mt-6 flex gap-3">
                {[
                  { icon: Linkedin, href: 'https://linkedin.com/company/glonextrack', label: 'LinkedIn' },
                  { icon: Youtube, href: 'https://youtube.com/@glonextrack', label: 'YouTube' },
                  { icon: Twitter, href: 'https://twitter.com/glonextrack', label: 'X' },
                ].map(s => (
                  <a key={s.label} href={s.href} className="rounded-lg glass-card p-2 text-slate-300 transition hover:text-neon-cyan hover:border-neon-cyan/20" aria-label={s.label}>
                    <s.icon className="h-4 w-4" />
                  </a>
                ))}
              </div>
            </div>
            {footerColumns.map((col) => (
              <div key={col.title}>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-300">{col.title}</h3>
                <ul className="mt-4 space-y-3">
                  {col.links.map((link) => (
                    <li key={link.href}>
                      <Link href={link.href} className="text-sm text-slate-300 transition hover:text-neon-cyan">{link.label}</Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="mt-12 flex flex-col items-center justify-between gap-4 border-t border-white/5 pt-8 md:flex-row">
            <p className="text-sm text-slate-300">&copy; {new Date().getFullYear()} GlonexTrack (Pty) Ltd. All rights reserved.</p>
            <div className="flex flex-wrap items-center gap-3">
              {[{ icon: ShieldCheck, label: 'POPIA' }, { icon: ShieldCheck, label: 'GDPR' }, { icon: ShieldCheck, label: 'ISO 27001' }].map((badge) => (
                <span key={badge.label} className="inline-flex items-center gap-1 rounded-full glass-card px-3 py-1 text-xs font-medium text-slate-300">
                  <badge.icon className="h-3 w-3 text-neon-cyan" />{badge.label}
                </span>
              ))}
            </div>
          </div>
          <div className="mt-8 pt-6 border-t border-white/5 flex flex-col items-center justify-between gap-4 text-xs text-slate-300 md:flex-row">
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5 text-neon-cyan shrink-0" />
                <span><strong className="text-white">SA HQ:</strong> Studio 5, Bridgewater 2, Century City, Cape Town (+27 87 094 0710)</span>
              </span>
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
                <span><strong className="text-white">DRC Branch:</strong> N°22, Avenue Toko, 13ème rue résidentielle, Motel Fikin, Limete (+243 960 964 767)</span>
              </span>
            </div>
            <div className="inline-flex items-center gap-1.5 shrink-0">
              <Mail className="h-3.5 w-3.5 text-neon-purple shrink-0" />
              <a href="mailto:info@glonextrack.com" className="hover:text-neon-cyan transition">info@glonextrack.com</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
